/**
 * Top-bar search (PM efb0e798). A search-box-shaped button (an icon on a
 * phone) that opens the global search in a dialog; Ctrl+K / Cmd+K opens it
 * from anywhere.
 *
 * The dialog holds the SAME SearchBar the sidebar used, fed the SAME index
 * (pages from the permission-filtered nav, reports, settings, help articles)
 * and the same server people search, so results and permissions are
 * unchanged. This component owns the shortcut; the SearchBar inside is told
 * not to register its own.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import SearchBar from "../common/SearchBar";
import type { SearchItem } from "../../search/searchTypes";
import type { ServerSearchType } from "../../api/searchAPI";
import { getCardBackground, getLightBorder } from "../../utils/themeUtils";

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

interface TopBarSearchProps {
  pages: SearchItem[];
  serverTypes: ServerSearchType[];
  /** phone: an icon instead of the search-box button */
  compact: boolean;
  isDarkMode: boolean;
}

const TopBarSearch: React.FC<TopBarSearchProps> = ({ pages, serverTypes, compact, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Ctrl+K / Cmd+K, from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Choosing a result navigates; the dialog closes with the new page.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`p-2 rounded-full transition-colors ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
          aria-label="Search"
          aria-haspopup="dialog"
        >
          <Search className="w-5 h-5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`flex items-center gap-2 w-56 lg:w-72 rounded-md border px-3 py-1.5 text-sm transition-colors ${
            isDarkMode
              ? "border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-300"
              : "border-gray-200 bg-gray-100 hover:bg-gray-200/70 text-gray-500"
          }`}
          aria-label="Search"
          aria-haspopup="dialog"
        >
          <Search className="w-4 h-4 flex-shrink-0 opacity-80" />
          <span className="flex-1 text-left opacity-80">Search…</span>
          <kbd className={`text-[10px] px-1.5 py-0.5 rounded border opacity-80 ${isDarkMode ? "border-gray-600" : "border-gray-300"}`} aria-hidden="true">
            {IS_MAC ? "⌘K" : "Ctrl K"}
          </kbd>
        </button>
      )}

      {/* Portaled to <body>: inside the fixed top bar it would share the
          bar's stacking context and could not cover the page. */}
      {open && createPortal(
        <div className="fixed inset-0 z-[80]" data-testid="search-dialog-layer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            className={`relative mx-auto mt-3 sm:mt-16 w-[calc(100%-1.5rem)] max-w-md rounded-lg border shadow-2xl p-2 ${getCardBackground(
              isDarkMode
            )} ${getLightBorder(isDarkMode)}`}
          >
            <SearchBar pages={pages} serverTypes={serverTypes} enableShortcut={false} autoFocus />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TopBarSearch;
